import { AIModel } from "@/ai/AIAssistant";
import { DCEvent, HttpEvent, httpGet, httpPost, HttpResponse } from "@/events/eventsDomain";
import { RootStoreData } from "@/events/rootStoreData";

export type CommentsApi = {
  addComment: (params: {
    data: AddCommentRequest;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => HttpEvent;

  updateComment: (params: {
    data: UpdateCommentRequest;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => HttpEvent;

  deleteComment: (params: {
    data: DeleteCommentRequest;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => HttpEvent;

  getCommentsOnProject: (params: {
    projectId: string;
    onSuccess: (
      res: HttpResponse<GetCommentsForProjectResponse>,
      state: RootStoreData,
    ) => DCEvent[];
    onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
  }) => HttpEvent;

  getCommentsOnDecision: (params: {
    projectId: string;
    decisionId: string;
    onSuccess: (data: CommentDTO[], state: RootStoreData) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => HttpEvent;
};

export const commentsApi: CommentsApi = {
  addComment(params: {
    data: AddCommentRequest;
    onSuccess: (data: CommentDTO[]) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): HttpEvent {
    return httpPost({
      url: `/comments/add`,
      data: params.data,
      onSuccess: (res) => params.onSuccess(res.data),
      onFailure: params.onFailure,
    });
  },

  updateComment(params: {
    data: UpdateCommentRequest;
    onSuccess: (data: CommentDTO[]) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): HttpEvent {
    return httpPost({
      url: `/comments/update`,
      data: params.data,
      onSuccess: (res) => params.onSuccess(res.data),
      onFailure: params.onFailure,
    });
  },

  deleteComment(params: {
    data: DeleteCommentRequest;
    onSuccess: (data: CommentDTO[]) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): HttpEvent {
    return httpPost({
      url: `/comments/delete`,
      data: params.data,
      onSuccess: (res) => params.onSuccess(res.data),
      onFailure: params.onFailure,
    });
  },

  getCommentsOnProject: ({ projectId, onSuccess, onFailure }) =>
    httpGet({
      url: `/comments/project/${projectId}`,
      onSuccess,
      onFailure,
    }),

  getCommentsOnDecision(params: {
    projectId: string;
    decisionId: string;
    onSuccess: (data: CommentDTO[], state: RootStoreData) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): HttpEvent {
    return httpGet({
      url: `/comments/project/${params.projectId}/decision/${params.decisionId}`,
      onSuccess: (res, state) => params.onSuccess(res.data, state),
      onFailure: params.onFailure,
    });
  },
};

export type AddCommentRequest = {
  projectId: string;
  decisionId?: string;
  commentText: string;
};

export type UpdateCommentRequest = {
  commentId: string;
  commentText: string;
};

export type DeleteCommentRequest = {
  commentId: string;
};

export type CommentDTO = {
  id: string;
  commentText: string;
  commenterName: string;
  isOwnComment: boolean;
  updatedAt: string;
};

export type GetCommentsForProjectResponse = {
  meetingEmailId?: string;
  comments: CommentDTO[];
};

export type EditCommentState = {
  aiAssistant?: AIModel;
  updateCommentId?: string;
  deleteCommentId?: string;
  showEditor?: boolean;
  commentText?: string;
};

export type CommentActions = {
  onSave?: (decisionId?: string) => void;
  onDeleteComment?: (commentId: string) => void;
  onEditComment?: (comment?: CommentDTO) => void;
  onToggleEditor?: () => void;
  onChange?: (text: string) => void;
  onCancel?: () => void;
  onConfirmDelete?: (commentId: string) => void;
};

export type CommentListProps = {
  decisionId?: string;
  comments: CommentDTO[];
  editState?: EditCommentState;
  actions: CommentActions;
  isLoading?: boolean;
};

export type CommentProps = {
  decisionId?: string;
  comment: CommentDTO;
  editState?: EditCommentState;
  actions: CommentActions;
};
